/**
 * Login rate limiting utilities (AUTH-VULN-03)
 * Per-IP and per-account rate limiting for brute-force protection
 */

interface RateLimitEntry {
  count: number;
  expiresAt: number;
}

const ipLimits = new Map<string, RateLimitEntry>();
const accountLimits = new Map<string, RateLimitEntry>();

const IP_WINDOW_MS = 15 * 60 * 1000; // 15 minutes
const ACCOUNT_WINDOW_MS = 15 * 60 * 1000; // 15 minutes

export const IP_MAX_ATTEMPTS = 5;
export const ACCOUNT_MAX_ATTEMPTS = 3;

function cleanup(map: Map<string, RateLimitEntry>) {
  const now = Date.now();
  for (const [key, val] of map) {
    if (val.expiresAt < now) map.delete(key);
  }
}

export function checkIpRateLimit(
  ip: string,
  maxAttempts: number = IP_MAX_ATTEMPTS,
  windowMs: number = IP_WINDOW_MS
): { allowed: boolean; remaining: number; retryAfterMs: number } {
  cleanup(ipLimits);
  const now = Date.now();
  const entry = ipLimits.get(ip);

  if (!entry) {
    ipLimits.set(ip, { count: 1, expiresAt: now + windowMs });
    return { allowed: true, remaining: maxAttempts - 1, retryAfterMs: 0 };
  }

  if (entry.count >= maxAttempts) {
    return { allowed: false, remaining: 0, retryAfterMs: entry.expiresAt - now };
  }

  entry.count++;
  return { allowed: true, remaining: maxAttempts - entry.count, retryAfterMs: 0 };
}

export function checkAccountRateLimit(
  email: string,
  maxAttempts: number = ACCOUNT_MAX_ATTEMPTS,
  windowMs: number = ACCOUNT_WINDOW_MS
): { allowed: boolean; remaining: number; retryAfterMs: number } {
  cleanup(accountLimits);
  const now = Date.now();
  const entry = accountLimits.get(email);

  if (!entry) {
    accountLimits.set(email, { count: 1, expiresAt: now + windowMs });
    return { allowed: true, remaining: maxAttempts - 1, retryAfterMs: 0 };
  }

  if (entry.count >= maxAttempts) {
    return { allowed: false, remaining: 0, retryAfterMs: entry.expiresAt - now };
  }

  entry.count++;
  return { allowed: true, remaining: maxAttempts - entry.count, retryAfterMs: 0 };
}

export function recordFailedAttempt(email: string): void {
  cleanup(accountLimits);
  const now = Date.now();
  const entry = accountLimits.get(email);
  if (entry) {
    entry.count++;
  } else {
    accountLimits.set(email, { count: 1, expiresAt: now + ACCOUNT_WINDOW_MS });
  }
}

export function resetAccountRateLimit(email: string): void {
  accountLimits.delete(email);
}
